<template>
  <li class="my-1" :key="`${itemName}${index}li`">
    <div
      :class="`flex flex-grow items-center cursor-pointer border border-solid border-grey rounded p-2 item ${isGrouper ? 'grouper font-bold' : 'leaf'} ${isRelated ? 'related' : ''}`"
      :title="isRelated ? 'This content was added automatically' : ''"
      >
      <feather-icon
        v-if="isGrouper"
        :icon="`${ isOpen ? 'MinusSquareIcon' : 'PlusSquareIcon' }`"
        svgClasses='h-4 w-4'
        class='flex items-start ml-1 mr-2'
        @click.stop="toggle"
      />
      <feather-icon
        v-if="!isGrouper"
        :icon="icons[item.content_type.toLowerCase()] ? icons[item.content_type.toLowerCase()].itemIcon : null"
        svgClasses='h-5 w-5 color-current text-grey'
        class='flex items-center ml-1 mr-2'
      />
      <div :class="`flex w-full ${!isGrouper ? 'ml-16' : canAddLeaf ? 'ml-8' : ''}`">

        <span :class="`${item.content_type}-type ${isGrouper ? 'grouper' : 'leaf'}`" v-if="!editName || !isGrouper">{{ itemName }}
          {{ getJwPlayerId }}<span class="waiting">{{ getVideoStatus }}</span>
        </span>

        <div class="flex w-4/5 grouper-field" v-if="editName && isGrouper">
          <vs-input class="w-full" size="small" v-model="groupName" @keyup.enter.native="saveEdit"></vs-input>
          <vs-button class="px-8 save-grouper-button" color="primary" size="small" icon="save" @click.stop="saveEdit">Save</vs-button>
          <vs-button class="px-8 cancel-grouper-button" type="border" color="primary" size="small" @click.stop="cancelEdit" icon="cancel">Cancel</vs-button>
        </div>
        
        <div class="ml-10 waiting-for-name" v-if="!itemName && !isGrouper">
          <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
        </div>
      </div>
      <div class="action-buttons flex ml-auto" v-if="!isGrouper || !editName">
        <feather-icon
          v-if="!isGrouper && showLinkToContent"
          icon="LinkIcon"
          svgClasses='h-5 w-5 color-current text-blue'
          class='flex items-center ml-1 mr-2'
          title="Open content in new tab"
          @click.stop="openLinkContent"
        />
        <feather-icon
          v-if="!isGrouper && canEditLeafs"
          icon="EditIcon"
          svgClasses='h-5 w-5 color-current text-blue'
          class='flex items-center ml-1 mr-2'
          @click.stop="openContent"
        />
        <feather-icon
          v-if="!isGrouper"
          icon="DeleteIcon"
          svgClasses='h-5 w-5 color-current text-danger'
          class='flex items-center ml-1 mr-2'
          @click.stop="deleteContent"
        />
        <feather-icon
          v-if="isGrouper"
          icon="EditIcon"
          svgClasses='h-5 w-5 color-current text-blue'
          class='flex items-center ml-1 mr-2'
          @click.stop="editName = true"
        />
        <feather-icon
          v-if="isGrouper"
          icon="DeleteIcon"
          svgClasses='h-5 w-5 color-current text-danger'
          class='flex items-center ml-1 mr-2'
          @click.stop="deleteGrouper"
        />
      </div>
    </div>
    <draggable
        class="list-group"
        v-show="isOpen"
        v-if="isGrouper"
        tag="ul"
        filter=".dont-move"
        v-model="content"
        v-bind="dragOptions"
        @start="drag = true"
        @end="drag = false"
      >
      <transition-group type="transition" :name="!drag ? 'flip-list' : null">
        <tree-structure-item
          v-for="(child, index) in item.content" :key="`${child.name}${index}` || `${child.uuid}${index}`"
          :item="child"
          :index="index"
          :parent="item"
          :videos="videos"
          :quizzes="quizzes"
          :exercises="exercises"
          :labs="labs"
          :iframes="iframes"
          :courses="courses"
          :groupers="groupers"
          :groupersCanAddLeaf="groupersCanAddLeaf"
          :canEditLeafs="canEditLeafs"
          :posibleLeafs="posibleLeafs"
          :namespace="namespace"
          :searchingRelatedOf="searchingRelatedOf"
          :showLinkToContent="showLinkToContent"
          @add-group="$emit('add-group', $event)"
          @add-leaf="$emit('add-leaf', $event)"
          @delete-group="$emit('delete-group', $event)"
          @delete-leaf="$emit('delete-leaf', $event)"
          @open-content="$emit('open-content', $event)"
          @rename-group="$emit('rename-group', $event)"
        />
        <div :key="`${item.name}${index}item-loading`" class="ml-5 mt-5 waiting-for-related" v-if="searchingRelatedOf.loading && isGrouper && searchingRelatedOf.group.name === item.name">
          <div class="lds-ring"><div></div><div></div><div></div><div></div></div> Searching related content...
        </div>
        <div :key="`${item.name}${index}item`" :class="`flex items-center m-5 dont-move ${canAddLeaf ? 'ml-20 pl-8' : ''}`" v-if="canAddLeaf">
          <div class="flex">
            <feather-icon
              icon="PlusCircleIcon"
              svgClasses='h-5 w-5 color-current text-grey'
              class='flex items-center ml-1 mr-2'
            />
          </div>
          <span v-for="leaf in posibleLeafs" :key="leaf" :class="`cursor-pointer mx-3 add ${leaf}-add add-leaf`" @click.stop="$emit('add-leaf', {type: leaf, parent: item})">
            <vx-tooltip :text="`Add ${leaf}`">
              <vs-button color="dark" type="line" icon-pack="feather" :icon="icons[leaf].icon"></vs-button>
            </vx-tooltip>
            <feather-icon
              v-if="isGrouper"
              :icon="icons[leaf].icon"
              svgClasses='h-5 w-5'
              class='flex items-center ml-1 mr-2 leaf'
            />
          </span>
        </div>
        <div :key="`${item.name}${index}topic`" v-if="!parent && item.content && !item.content.length && !canAddLeaf" :class="`flex cursor-pointer w-1/4 mb-5 add ml-16 mt-5 add-topic dont-move`" @click.stop="addTopicAfter">
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses='h-5 w-5 color-current text-grey'
            class='flex items-center ml-1 mr-2'
          />
          topic
        </div>
        <div :key="`${item.name}${index}grouporleaf`" :class="`flex cursor-pointer w-1/4 mb-5 add mt-5 dont-move add-${item.content_type} ${canAddLeaf ? 'ml-16' : ''}`" @click.stop="addGrouperAfter">
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses='h-5 w-5 color-current text-grey'
            class='flex items-center ml-1 mr-2'
          />
          {{ item.content_type }}
        </div>
        </transition-group>
    </draggable>
  </li>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: "TreeStructureItem",
  components: {
      draggable,
  },
  props: {
    item: Object,
    parent: Object,
    videos: Object,
    quizzes: Object,
    exercises: Object,
    labs: Object,
    iframes: Object,
    courses: Object,
    index: Number,
    posibleLeafs: {
      type: Array,
      default: () => {
        return ['video', 'quiz', 'exercise', 'lab', 'iframe']
      }
    },
    groupersCanAddLeaf:{
      type: Array,
      default: () => {
        return ['topic']
      }
    },
    groupers: {
      type: Array,
      default: () => {
        return ['group', 'topic']
      }
    },
    canEditLeafs: {
      type: Boolean,
      default: () => {
        return true
      }
    },
    namespace: String,
    searchingRelatedOf: {
      type: Object,
      default: () => {
        return {
          loading: false,
          group: null,
        }
      }
    },
    showLinkToContent: {
      type: Boolean,
      default: () => {
        return false
      }
    },
  },
  data () {
    return {
      isOpen: true,
      editName: Boolean(!this.item.name || false),
      groupName: this.item.name ? this.item.name : '',
      drag: false,
      icons: {
        video: {
          icon: 'icon-video',
          itemIcon: 'VideoIcon'
        },
        quiz: {
          icon: 'icon-check-square',
          itemIcon: 'CheckSquareIcon'
        },
        exercise: {
          icon: 'icon-terminal',
          itemIcon: 'TerminalIcon'
        },
        lab: {
          icon: 'icon-github',
          itemIcon: 'GithubIcon'
        },
        iframe: {
          icon: 'icon-external-link',
          itemIcon: 'ExternalLinkIcon'
        },
        course: {
          icon: 'icon-book',
          itemIcon: 'BookIcon'
        }
      }
    }
  },
  computed: {
    isGrouper() {
      return this.groupers.includes(this.item.content_type)
    },
    canAddLeaf() {
      return this.groupersCanAddLeaf.includes(this.item.content_type)
    },
    itemName () {
      if (this.videos && this.item.content_type === 'video' && this.videos[this.item.uuid]) {
        return `${this.videos[this.item.uuid].name}`
      }
      if (this.courses && this.item.content_type === 'course' && this.courses[this.item.uuid]) {
        return this.courses[this.item.uuid].name
      }
      if (this.exercises && this.item.content_type === 'exercise' && this.exercises[this.item.uuid]) {
        return this.exercises[this.item.uuid].name
      }
      if (this.labs && this.item.content_type === 'lab' && this.labs[this.item.uuid]) {
        return this.labs[this.item.uuid].name
      }
      if (this.iframes && this.item.content_type === 'iframe' && this.iframes[this.item.uuid]) {
        return this.iframes[this.item.uuid].name
      }
      if (this.quizzes && this.item.content_type === 'quiz' && this.quizzes[this.item.uuid]) {
        return `Quiz - ${this.item.uuid}`
      }
      return this.item.name
    },
    getVideoStatus() {
      if (this.item.content_type === 'video' && this.videos && this.videos[this.item.uuid] && this.videos[this.item.uuid].status === 'waiting') {
        return ' - Waiting'
      }
      return ''
    },
    content: {
      get() {
        return this.item.content
      },
      set(value) {
        this.$store.dispatch(`${this.namespace}/updateContentStructure`, {
          item: this.item,
          newContent: value
        });
      }
    },
    dragOptions() {
      return {
        animation: 200,
        group: this.item.content_type,
        disabled: false,
        ghostClass: "ghost"
      };
    },
    getJwPlayerId() {
      if (this.item.content_type === 'video' && this.videos && this.videos[this.item.uuid] && this.videos[this.item.uuid].jwplayer_id) {
        return  '- ' + this.videos[this.item.uuid].jwplayer_id
      }
      return ''
    },
    isRelated() {
      return this.item.related
    }
  },
  methods: {
    toggle: function () {
      if (this.isGrouper) {
        this.isOpen = !this.isOpen
      }
    },
    openContent: function () {
      this.$emit('open-content', this.item)
    },
    deleteContent: function () {
      this.$emit('delete-leaf', {item: this.item, parent: this.parent})
    },
    cancelEdit () {
      if (!this.groupName) return
      this.editName = false
      this.groupName = this.item.name ? this.item.name : ''
    },
    saveEdit() {
      if (!this.groupName) return
      this.$emit('rename-group', {
        oldName: this.item.name,
        newName: this.groupName,
        type: this.item.content_type,
        item: this.item
      })
      this.editName = false
      this.groupName = this.item.name ? this.item.name : ''
    },
    //this is for the first topic in the group
    addTopicAfter () {
      this.$emit('add-group',{
        parent: this.item,
        index: 0,
        type: 'topic'
      })
    },
    addGrouperAfter () {
      this.$emit('add-group',{
        parent: this.parent,
        index: this.index + 1,
        type: this.item.content_type
      })
    },
    deleteGrouper () {
      this.$emit('delete-group',{
        parent: this.parent,
        index: this.index
      })
    },
    openLinkContent() {
      let path = ''
      if (this.item.content_type.toLowerCase() === 'learning path') {
        path = 'edit-lp'
      } else {
        path = `edit-${this.item.content_type.toLowerCase()}`
      }

      let routeData = this.$router.resolve({name: path, params: { id: this.item.uuid }});
      window.open(routeData.href, '_blank');
    }
  }
}
</script>

<style lang="scss" scoped>
.item.related {
  background-color: #efeeff;
}
</style>